// Helpers that turn the wizard's completion map into the concrete list of
// required questions still blocking a step. The wizard already computes a
// `completionByQuestion` map (question id -> reason | null); these functions
// expose *which* required questions are incomplete and *why*, so the UI can
// tell the company what to do instead of just disabling "Continue".

import type { CompletionReason } from './visibility.ts';
import type { Question } from './types.ts';

export type StepBlocker = { question: Question; reason: CompletionReason };

/**
 * The required questions in `questions` that are not yet complete, preserving
 * the input order. A question is a blocker when it is `required` and its entry
 * in `completionByQuestion` is a non-null reason. Questions with a `null`
 * reason (complete) or no entry at all (hidden / not in the visible set) are
 * excluded.
 */
export function stepBlockers(
  questions: Question[],
  completionByQuestion: Map<string, CompletionReason | null>,
): StepBlocker[] {
  const blockers: StepBlocker[] = [];
  for (const q of questions) {
    if (!q.required) continue;
    const reason = completionByQuestion.get(q.id);
    // `!= null` excludes both null (complete) and undefined (not in the map).
    if (reason != null) blockers.push({ question: q, reason });
  }
  return blockers;
}

/**
 * A short, action-oriented instruction for a blocking question, shown in the
 * "Still needed" panel next to the question's label.
 */
export function blockerHint(reason: CompletionReason): string {
  switch (reason) {
    case 'no_answer':
      return 'Needs an answer';
    case 'upload_required':
      return 'Needs a document uploaded';
    case 'adoption_required':
      return 'Adopt or upload the linked policy';
    case 'video_unwatched':
      return 'Watch the video, then mark it as watched';
  }
}
